import type { AuthResponse, LoginInput, SessionUser } from '@epos/contracts';
import { createContext, ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api, authenticationExpiredEvent, quickPinApi, sessionUserStore, tokenStore } from '../api';
import { authenticateWithPasskey } from './passkeys';
import { referenceCache } from '../referenceCache';

type AuthContextValue = { user?: SessionUser; loading: boolean; login(input: LoginInput): Promise<AuthResponse>; loginWithPasskey(email: string): Promise<void>; loginWithPin(handle: string, pin: string): Promise<void>; finishAuthentication(response:AuthResponse):void; logout(): void };
const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const savedUser = tokenStore.get() ? sessionUserStore.get() : undefined;
  const [user, setUser] = useState<SessionUser | undefined>(savedUser);
  const [loading, setLoading] = useState(Boolean(tokenStore.get()) && !savedUser);
  useEffect(() => {
    if (!tokenStore.get()) return;
    api.auth.me()
      .then(current => { sessionUserStore.set(current); setUser(current); })
      .catch(() => { sessionUserStore.clear(); tokenStore.clear(); setUser(undefined); })
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => {
    const expire = () => { referenceCache.clearCurrentScope(); sessionUserStore.clear(); queryClient.clear(); setUser(undefined); setLoading(false); };
    window.addEventListener(authenticationExpiredEvent, expire);
    return () => window.removeEventListener(authenticationExpiredEvent, expire);
  }, [queryClient]);
  const value = useMemo<AuthContextValue>(() => { const accept=(response:{token:string;user:SessionUser})=>{queryClient.clear();tokenStore.set(response.token);sessionUserStore.set(response.user);setUser(response.user)}; return ({ user, loading, async login(input) { const response=await api.auth.login(input); if((response.authenticationStage??'complete')==='complete') accept(response); return response; }, async loginWithPasskey(email) { accept(await authenticateWithPasskey(email)); }, async loginWithPin(handle,pin) { accept(await quickPinApi.login(handle,pin)); }, finishAuthentication(response){accept(response)}, logout() { referenceCache.clearCurrentScope(); sessionUserStore.clear(); tokenStore.clear(); queryClient.clear(); setUser(undefined); if (window.eposDesktop) { void window.eposDesktop.session.returnToLauncher().catch(() => window.location.replace("/login")); } else { window.location.replace("/login"); } } }); }, [user, loading, queryClient]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth must be used within AuthProvider');
  return value;
}
